import { idNameSchema } from '@/modules/ISM/api/companies/schema';
import { participantSchema } from '@/modules/ISM/api/participants/schema';
import { userSchema } from '@/modules/ISM/api/users/schema';
import { vesselSchema } from '@/modules/ISM/api/vessels/schema';
import { z } from 'zod';

const requiredStringSchema = z.string().min(1);

const _drillTypeDataSchema = z.object({
  Instructions: z.string().optional(),
  Scenario: z.string().optional(),
  VerificationOptions: z.string().optional()
});

const verificationOptionSchema = z.object({
  Name: requiredStringSchema,
  Id: requiredStringSchema,
  Values: z.array(z.string()),
  Colors: z.array(z.string()),
  SelectedValue: z.string().optional(),
  SelectedColor: z.string().optional()
});

const drillDataSchema = z.object({
  Instructions: z.string().optional(),
  Scenario: z.string().optional(),
  DrillOptionsTitle: z.string().optional(),
  DrillOptionsComments: z.string().optional(),
  DrillOptions: z.array(verificationOptionSchema).optional()
});

const drillTypeSchema = z.object({
  Id: requiredStringSchema,
  Name: requiredStringSchema,
  RequiresVerification: z.boolean().optional(),
  Visibility: z.number().optional(),
  Owner: idNameSchema.optional(),
  Data: z.string().optional(),
  Scenario: z.string().optional(),
  Vessels: z.string().optional(),
  NewDrillByVessel: z.boolean().optional(),
  CreatedBy: z.string().optional(),
  CreatedDate: z.string().optional(),
  CreatedDateLocal: z.string().optional(),
  DeletedBy: z.string().optional(),
  DeletedDate: z.string().optional(),
  DeletedDateLocal: z.string().optional(),
  IsDeleted: z.boolean().optional(),
  UpdatedBy: z.string().optional(),
  UpdatedDate: z.string().optional(),
  UpdatedDateLocal: z.string().optional(),
  pivot: z
    .object({
      vessel_id: requiredStringSchema,
      drill_type_id: requiredStringSchema
    })
    .optional()
});

const vesselDetailsSchema = vesselSchema;

const securitySchema = z.object({
  CanDelete: z.boolean(),
  CanSave: z.boolean(),
  CanVerify: z.boolean()
});

const drillExSchema = z.object({
  Id: z.string().optional(),
  Vessel: vesselDetailsSchema.optional(),
  RejectComments: z.string().optional(),
  DrillType: drillTypeSchema.optional(),
  DrillTypeData: z.string().optional(),
  DueDateFrom: z.string().optional(),
  DueDate: z.string().optional(),
  Name: z.string().optional(),
  ActionToBeTakenBy: z.union([idNameSchema, z.string()]).optional(),
  Owner: z.union([idNameSchema, z.string()]).optional(),
  Verifier: z.union([idNameSchema, z.string()]).optional(),
  VisibleBy: z.union([idNameSchema, z.string()]).optional(),
  Status: z.string().optional(),
  Port: idNameSchema.optional(),
  StartTimeLocal: z.string().optional(),
  EndTimeLocal: z.string().optional(),
  Timezone: z.string().optional(),
  Instructions: z.string().optional(),
  DrillScenario: z.string().optional(),
  ParticipantList: z.array(participantSchema).optional(),
  Participants: z.string().optional(),
  Security: securitySchema.optional(),
  ExternalCode: z.string().optional(),
  OriginalOwner: z.union([idNameSchema, z.string()]).optional(),
  DrillData: drillDataSchema.optional(),
  Verification: z.string().optional(),
  Data: z.string().optional()
});

const apiResponseSchema = (dataSchema: z.ZodType) =>
  z.object({
    Data: dataSchema
  });

export { drillExSchema, drillTypeSchema, drillDataSchema, verificationOptionSchema };

export const drillExArraySchema = z.array(drillExSchema);
export const drillTypeArraySchema = z.array(drillTypeSchema);
export const participantArraySchema = z.array(participantSchema);
export const userArraySchema = z.array(userSchema);
export const idNameArraySchema = z.array(idNameSchema);
export const vesselArraySchema = z.array(vesselSchema);

export const drillExApiResponseSchema = apiResponseSchema(drillExSchema);
export const drillExArrayApiResponseSchema = apiResponseSchema(drillExArraySchema);
export const drillTypeArrayApiResponseSchema = apiResponseSchema(drillTypeArraySchema);
export const participantArrayApiResponseSchema = apiResponseSchema(participantArraySchema);
export const userArrayApiResponseSchema = apiResponseSchema(userArraySchema);
export const idNameArrayApiResponseSchema = apiResponseSchema(idNameArraySchema);
export const vesselArrayApiResponseSchema = apiResponseSchema(vesselArraySchema);

export type DrillEx = z.infer<typeof drillExSchema>;
export type DrillType = z.infer<typeof drillTypeSchema>;
export type DrillData = z.infer<typeof drillDataSchema>;
export type VerificationOption = z.infer<typeof verificationOptionSchema>;
export type DrillTypeData = z.infer<typeof _drillTypeDataSchema>;

// Types imported above to avoid duplicate import/export warnings

export interface ApiResponse<T> {
  Data: T;
}
